<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import type { Asset, TextMediaColumnsPageSectionBlock } from '@storyblok-types';
  import { PageSection } from '@athena-lib/Page';
  import { Text } from '@athena-lib/Text';
  import { Column, Columns } from '@athena-lib/Columns';
  import clsx from 'clsx';
  import { isRichTextEmpty } from '../RichText/types';
  import PageSectionHeading from './shared/PageSectionHeading.svelte';
  import { getFocalPointClasses } from './shared/image-utils';
  import ImageOrVideo from './ImageOrVideo.svelte';

  let { blok }: { blok: TextMediaColumnsPageSectionBlock } = $props();

  type ColumnT = {
    headingText:
      | (typeof blok)['column1HeadingText']
      | (typeof blok)['column2HeadingText']
      | (typeof blok)['column3HeadingText'];
    headingTag:
      | (typeof blok)['column1HeadingTag']
      | (typeof blok)['column2HeadingTag']
      | (typeof blok)['column3HeadingTag'];
    headingSize:
      | (typeof blok)['column1HeadingSize']
      | (typeof blok)['column2HeadingSize']
      | (typeof blok)['column3HeadingSize'];
    media:
      | (typeof blok)['column1Media']
      | (typeof blok)['column2Media']
      | (typeof blok)['column3Media'];
    mediaFit:
      | (typeof blok)['column1MediaFit']
      | (typeof blok)['column2MediaFit']
      | (typeof blok)['column3MediaFit'];
    description:
      | (typeof blok)['column1Description']
      | (typeof blok)['column2Description']
      | (typeof blok)['column3Description'];
    ctaButtonText:
      | (typeof blok)['column1CtaButtonText']
      | (typeof blok)['column2CtaButtonText']
      | (typeof blok)['column3CtaButtonText'];
    ctaButtonColor:
      | (typeof blok)['column1CtaButtonColor']
      | (typeof blok)['column2CtaButtonColor']
      | (typeof blok)['column3CtaButtonColor'];
    ctaButtonLink:
      | (typeof blok)['column1CtaButtonLink']
      | (typeof blok)['column2CtaButtonLink']
      | (typeof blok)['column3CtaButtonLink'];
    ctaButtonClasses:
      | (typeof blok)['column1CtaButtonExtraClasses']
      | (typeof blok)['column2CtaButtonExtraClasses']
      | (typeof blok)['column3CtaButtonExtraClasses'];
    addToCart:
      | (typeof blok)['column1AddToCart']
      | (typeof blok)['column2AddToCart']
      | (typeof blok)['column3AddToCart'];
    autoplay:
      | (typeof blok)['column1Autoplay']
      | (typeof blok)['column2Autoplay']
      | (typeof blok)['column3Autoplay'];
    productType:
      | (typeof blok)['column1ProductType']
      | (typeof blok)['column2ProductType']
      | (typeof blok)['column3ProductType'];
    productId:
      | (typeof blok)['column1ProductId']
      | (typeof blok)['column2ProductId']
      | (typeof blok)['column3ProductId'];
    textUnderMedia:
      | (typeof blok)['column1TextUnderMedia']
      | (typeof blok)['column2TextUnderMedia']
      | (typeof blok)['column3TextUnderMedia'];
  };

  const imageAspectClasses = {
    '1:1': 'aspect-square w-full',
    '2:1': 'aspect-2/1',
    '3:2': 'aspect-3/2 w-full',
    '2:3': 'aspect-2/3 w-full',
  };

  const imageWidthClasses = {
    large: 'w-full',
    medium: 'w-[75%]',
    small: 'w-[50%]',
  };

  const hasSecondColumn = $derived(
    !!(
      blok.column2CtaButtonText ||
      blok.column2HeadingText ||
      !isRichTextEmpty(blok.column2Description) ||
      blok.column2Media?.id
    ),
  );

  const hasThirdColumn = $derived(
    !!(
      blok.column3CtaButtonText ||
      blok.column3HeadingText ||
      !isRichTextEmpty(blok.column3Description) ||
      blok.column3Media?.id
    ),
  );

  const columnWidth = $derived(hasThirdColumn ? '1/3' : hasSecondColumn ? '1/2' : undefined);

  const aspectRatioClass = $derived(imageAspectClasses[blok.imageHeight ?? '1:1']);
</script>

{#snippet text(column: ColumnT)}
  <div class="flex h-full items-center pl-medium pr-medium">
    <PageSectionHeading
      headingText={column.headingText}
      headingTag={column.headingTag}
      headingSize={column.headingSize}
      description={column.description}
      mobileAlignment={blok.mobileAlignment}
      desktopAlignment={blok.desktopAlignment}
      ctaButton={{
        text: column.ctaButtonText,
        color: column.ctaButtonColor,
        link: column.ctaButtonLink,
        addToCart: column.addToCart,
        productType: column.productType,
        productId: column.productId,
        productImageUrl: column.media?.filename,
        class: column.ctaButtonClasses,
      }}
    />
  </div>
{/snippet}

{#snippet columnMedia(
  media: Asset | undefined,
  textUnderMedia: string | undefined,
  fit: string | number | undefined,
  autoplay?: boolean,
)}
  <div class="flex h-full items-center">
    {#if media}
      <div class="flex justify-center">
        <div class={clsx('relative', imageWidthClasses[blok.imageSize || 'large'])}>
          <ImageOrVideo
            autoplay={autoplay ?? false}
            asset={media}
            class={aspectRatioClass}
            innerImageClass={clsx(
              getFocalPointClasses(media),
              fit === 'cover' ? 'object-cover' : 'object-contain',
              aspectRatioClass,
            )}
          />
          {#if textUnderMedia?.trim()}
            <Text class="absolute bottom-1 left-1 right-1 bg-st-white bg-opacity-90 p-1" italic>
              {textUnderMedia}
            </Text>
          {/if}
        </div>
      </div>
    {/if}
  </div>
{/snippet}

{#snippet column(column: ColumnT)}
  <div
    class={clsx(
      'flex flex-col gap-large',
      !blok.mobileImageFirst && 'max-md:flex-col-reverse',
      !blok.desktopImageFirst && 'md:flex-col-reverse',
    )}
  >
    <div>
      {@render columnMedia(column.media, column.textUnderMedia, column.mediaFit, column.autoplay)}
    </div>
    <div>
      {@render text(column)}
    </div>
  </div>
{/snippet}

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{ top: blok.topPadding ?? 'medium', bottom: blok.bottomPadding ?? 'medium' }}
  backgroundColor={blok.backgroundColor ?? 'light'}
>
  <Columns collapseBelow={hasThirdColumn ? 'lg' : 'md'} space="xxlarge">
    <Column width={columnWidth}>
      {@render column({
        headingText: blok.column1HeadingText,
        headingTag: blok.column1HeadingTag,
        headingSize: blok.column1HeadingSize,
        media: blok.column1Media,
        mediaFit: blok.column1MediaFit,
        description: blok.column1Description,
        ctaButtonText: blok.column1CtaButtonText,
        ctaButtonColor: blok.column1CtaButtonColor,
        ctaButtonLink: blok.column1CtaButtonLink,
        ctaButtonClasses: blok.column1CtaButtonExtraClasses,
        textUnderMedia: blok.column1TextUnderMedia,
        addToCart: blok.column1AddToCart,
        productType: blok.column1ProductType,
        productId: blok.column1ProductId,
        autoplay: blok.column1Autoplay,
      })}
    </Column>

    {#if hasSecondColumn}
      <Column width={columnWidth}>
        {@render column({
          headingText: blok.column2HeadingText,
          headingTag: blok.column2HeadingTag,
          headingSize: blok.column2HeadingSize,
          media: blok.column2Media,
          mediaFit: blok.column2MediaFit,
          description: blok.column2Description,
          ctaButtonText: blok.column2CtaButtonText,
          ctaButtonColor: blok.column2CtaButtonColor,
          ctaButtonLink: blok.column2CtaButtonLink,
          ctaButtonClasses: blok.column2CtaButtonExtraClasses,
          textUnderMedia: blok.column2TextUnderMedia,
          addToCart: blok.column2AddToCart,
          productType: blok.column2ProductType,
          productId: blok.column2ProductId,
          autoplay: blok.column2Autoplay,
        })}
      </Column>
    {/if}

    {#if hasThirdColumn}
      <Column width={columnWidth}>
        {@render column({
          headingText: blok.column3HeadingText,
          headingTag: blok.column3HeadingTag,
          headingSize: blok.column3HeadingSize,
          media: blok.column3Media,
          mediaFit: blok.column3MediaFit,
          description: blok.column3Description,
          ctaButtonText: blok.column3CtaButtonText,
          ctaButtonColor: blok.column3CtaButtonColor,
          ctaButtonLink: blok.column3CtaButtonLink,
          ctaButtonClasses: blok.column3CtaButtonExtraClasses,
          textUnderMedia: blok.column3TextUnderMedia,
          addToCart: blok.column3AddToCart,
          productType: blok.column3ProductType,
          productId: blok.column3ProductId,
          autoplay: blok.column3Autoplay,
        })}
      </Column>
    {/if}
  </Columns>
</PageSection>
